import { Component, inject, signal } from "@angular/core"
import { ActivatedRoute, Router } from "@angular/router"
import { MatIconModule } from "@angular/material/icon"
import { DatePipe } from "@angular/common"
import { toObservable } from "@angular/core/rxjs-interop"
import { catchError, finalize, of, switchMap, takeUntil } from "rxjs"
import { AppBarComponent } from "../../../../shared/components/app-bar/app-bar.component"
import { MainContentComponent } from "../../../../shared/components/main-content/main-content.component"
import { SeoComponent } from "../../../../shared/components/seo/seo.component"
import { ERoutes } from "../../../../shared/constants/routes.enum"
import {
  RecommendationTimeSlot,
  INTERNET_ACTIVITIES,
  SpotRecommendationService,
} from "../../../services/spot-recommendation.service"
import { ISpot, ISpotDetail } from "../../../interfaces/spot-recommendation.interface"

@Component({
  selector: "app-spot-details",
  imports: [AppBarComponent, MainContentComponent, MatIconModule, DatePipe],
  templateUrl: "./spot-details.component.html",
  styleUrl: "./spot-details.component.scss",
})
export class SpotDetailsComponent extends SeoComponent {
  private readonly route = inject(ActivatedRoute)
  private readonly router = inject(Router)
  private readonly recommendations = inject(SpotRecommendationService)

  readonly activeTime = signal<RecommendationTimeSlot>("now")
  readonly loading = signal(true)
  readonly error = signal(false)
  readonly details = signal<ISpotDetail | null>(null)
  readonly timeFilters: { id: RecommendationTimeSlot; label: string }[] = [
    { id: "now", label: "Now" },
    { id: "morning", label: "Morning" },
    { id: "lunch", label: "Lunch" },
    { id: "afternoon", label: "Afternoon" },
    { id: "evening", label: "Evening" },
    { id: "night", label: "Night" },
  ]

  constructor() {
    super()
    this.title = "Spot Details"
    const spotId = Number(this.route.snapshot.queryParamMap.get("spot"))
    const timeSlot = this.route.snapshot.queryParamMap.get("time_slot")
    if (this.timeFilters.some((item) => item.id === timeSlot)) {
      this.activeTime.set(timeSlot as RecommendationTimeSlot)
    }

    toObservable(this.activeTime)
      .pipe(
        switchMap((timeSlot) => {
          if (!Number.isInteger(spotId) || spotId <= 0) {
            this.loading.set(false)
            this.error.set(true)
            return of(null)
          }
          this.loading.set(true)
          this.error.set(false)
          return this.recommendations.getSpotDetails(spotId, timeSlot).pipe(
            catchError(() => {
              this.error.set(true)
              return of(null)
            }),
            finalize(() => this.loading.set(false)),
          )
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((details) => this.details.set(details))
  }

  setTime(time: RecommendationTimeSlot): void {
    this.activeTime.set(time)
  }

  back(): void {
    this.router.navigate([this.i18nStore.routeRoot, ERoutes.SPOTS], {
      queryParams: { view: "analysis" },
    })
  }

  location(spot: ISpot): string {
    const parts = [
      spot.building?.commonName,
      spot.building?.compound,
      spot.floorNumber != null ? `Floor ${spot.floorNumber}` : null,
    ]
      .filter(Boolean)
      .map((part) => String(part).trim())

    const uniqueParts = parts.filter(
      (part, index) =>
        parts.findIndex(
          (candidate) => candidate.toLocaleLowerCase() === part.toLocaleLowerCase(),
        ) === index,
    )

    if (!uniqueParts.length && spot.description) {
      return spot.description
    }

    return uniqueParts.join(" · ")
  }

  activityLabel(useCase: string): string {
    return INTERNET_ACTIVITIES.find((item) => item.id === useCase)?.label ?? useCase
  }

  confidence(value: number | undefined): string {
    const percentage = Math.round((value ?? 0) * 100)
    return `${percentage}% confidence`
  }

  availability(value: string | null | undefined): string {
    return value ? value.replaceAll("_", " ").toLowerCase() : "No data"
  }

  utilization(value: number | null | undefined): string {
    return value == null ? "—" : `${Math.round(value * 100)}%`
  }
}
